import React, { useState } from 'react';
import './ContactForm.css';

const ContactForm = ({ config }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    agreeToInfo: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Set custom validation messages for HTML5 validation
    const input = e.target;
    if (input.validity.valueMissing) {
      input.setCustomValidity('Please fill out this field.');
    } else if (input.type === 'email' && input.validity.typeMismatch) {
      input.setCustomValidity('Please include an "@" in the email address.');
    } else if (input.type === 'checkbox' && !checked) {
      input.setCustomValidity('You must check this box to continue.');
    } else {
      input.setCustomValidity('');
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    let newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    // Message is optional, no validation required
    
    if (!formData.agreeToInfo) {
      newErrors.agreeToInfo = 'You must agree to receive additional information';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      
      try {
        // 准备表单数据 - 使用 FormData 格式
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('message', formData.message || 'No message provided');
        formDataToSend.append('_subject', 'New Contact Form Submission - Nexus Living');
        formDataToSend.append('_template', 'table');
        formDataToSend.append('_captcha', 'false');
        formDataToSend.append('_autoresponse', 'Thank you for contacting Nexus Living! We have received your message and will get back to you soon.');
        formDataToSend.append('Form Type', 'Contact Form');
        formDataToSend.append('Submission Time', new Date().toLocaleString());
        formDataToSend.append('Agreed to Info', formData.agreeToInfo ? 'Yes' : 'No');

        // 发送邮件到FormSubmit (使用 FormData 格式避免 CORS 问题)
        await fetch('https://formsubmit.co/info@nexusliving.ke', {
          method: 'POST',
          body: formDataToSend,
          mode: 'no-cors' // 避免 CORS 错误
        });

        // 显示成功消息
        setSubmitSuccess(true);
        
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          agreeToInfo: false
        });
        
        // 3秒后隐藏成功消息
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
        
      } catch (error) {
        console.error('Error sending form:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
        {submitSuccess && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#5277F1',
            color: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            fontWeight: '500'
          }}>
            Thank you for your message! We will get back to you soon.
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="name" className="form-label">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="How we address you"
            className={`form-input ${errors.name ? 'error' : ''}`}
            disabled={isSubmitting}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="phone" className="form-label">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Mobile phone number to contact you"
            className={`form-input ${errors.phone ? 'error' : ''}`}
            disabled={isSubmitting}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Fill in your email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            disabled={isSubmitting}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="message" className="form-label">Message</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Fill in other requirements"
            rows="5"
            className="form-textarea"
            disabled={isSubmitting}
          />
          {errors.agreeToInfo && <span className="error-message">{errors.agreeToInfo}</span>}
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="agreeToInfo"
            name="agreeToInfo"
            checked={formData.agreeToInfo}
            onChange={handleChange}
            className={`form-checkbox ${errors.agreeToInfo ? 'error' : ''}`}
            disabled={isSubmitting}
          />
          <label htmlFor="agreeToInfo" className="checkbox-label">
            I agree to ToS and Privacy Policy
          </label>
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Submit'}
        </button>
      </form>
  );
};

export default ContactForm;
